<template>
  <v-card flat elevation="0">
    <DrawerToolbar
      title="Create a New Client"
      :actionDisabled="!form.valid || pspsSelectionBlocked"
      admin
      @close="close"
    />
    <div key="0">
      <v-card-text class="d-flex flex-column text-left px-4 py-6">
        <Typography variant="subtitle-1" class="pb-4">Branding & Basics</Typography>
        <v-img
          v-if="imageUpload.imageUrl || client.companyPicUrl"
          :src="imageUpload.imageUrl || client.companyPicUrl"
          max-height="150"
          max-width="300px"
          contain
          @click="displayImageSelector"
          @drop.prevent="imageFileChanged($event, true)"
          @dragover.prevent
          class="cursor-pointer mx-auto box-border-global"
        />
        <v-img
          v-else
          src="@/assets/placeholder_image.png"
          max-height="150"
          max-width="300px"
          contain
          @click="displayImageSelector"
          @drop.prevent="imageFileChanged($event, true)"
          @dragover.prevent
          class="cursor-pointer mx-auto box-border-global"
        />
        <div class="d-flex justify-center pt-6">
          <Button class="mx-3" text color="brandPrimary" @click="displayImageSelector">
            Upload Logo
          </Button>
          <Button
            v-if="client.companyPicUrl || imageUpload.imageUrl"
            class="mr-2"
            text
            color="error"
            @click="deleteImage"
          >
            Delete Logo
          </Button>
        </div>
        <input
          id="uploader"
          ref="uploader"
          class="d-none"
          type="file"
          accept="image/*"
          @change="imageFileChanged($event, false)"
        />

        <v-form ref="form" v-model="form.valid" class="full-width mt-6">
          <v-text-field
            v-model="client.clientName"
            :rules="formRules.stringRequired(255)"
            label="Client Name*"
            color="brandPrimary"
            required
            outlined
          ></v-text-field>
          <v-text-field
            v-model="client.emailDomain"
            :rules="formRules.stringOptional(255)"
            label="Email Domain Name"
            color="brandPrimary"
            outlined
          ></v-text-field>
          <v-select
            v-model="client.initialRouteOnLoad"
            label="Default Page"
            :items="defaultPages"
            item-value="value"
            item-text="label"
            color="brandPrimary"
            outlined
          ></v-select>
          <v-select
            v-model="client.onboardingLanguage"
            label="Language Used in Onboarding"
            :items="onboardingLanguages"
            item-value="value"
            item-text="label"
            color="brandPrimary"
            outlined
          ></v-select>
          <Typography variant="subtitle-1" class="pb-4">Access & Relationships</Typography>
          <v-autocomplete
            v-model="client.parentClientId"
            label="Parent client"
            :items="clients"
            item-value="clientId"
            item-text="formattedName"
            color="brandPrimary"
            outlined
            clearable
          ></v-autocomplete>
          <v-text-field
            v-model="client.onboardingToken"
            :rules="[
              (v) => !!(!v || (v && v.length > 5)) || 'Value should be 6+ characters long',
              (v) => !!(!v || (v && !v.includes('/'))) || 'Cannot contain \'/\'',
              (v) => !!(!v || (v && v.length < 128)) || 'Value should be shorter',
            ]"
            hint="This will appear at the end of your sign-up URL (e.g., yourcompany)."
            persistent-hint
            label="Custom Sign Up Link (URL Extension)"
            color="brandPrimary"
            outlined
          ></v-text-field>

          <Typography variant="subtitle-1" class="pb-1 pt-10">Program Options</Typography>
          <v-switch
            v-model="client.hideDefaultPlatformLogo"
            label="Hide default platform logo"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.procoreEnabled"
            label="Procore client"
            color="brandPrimary"
            hide-details
          ></v-switch>

          <v-switch
            v-model="client.displayUniversalLearning"
            label="Display universal learning"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.displayMobilePopup"
            label="Display mobile popup on web"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.magicLinksEnabled"
            label="Magic links enabled"
            color="brandPrimary"
            hide-details
          ></v-switch>

          <h3 class="pb-1 pt-10">Visible Pages</h3>
          <v-switch
            v-model="client.displayActivityPage"
            label="Display activity page"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.displayWalletPage"
            label="Display wallet page"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.displayProgramPage"
            label="Display program page"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.displayProgramDashboardPage"
            label="Display program dashboard page"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.displayClaimPage"
            label="Display claim pages"
            color="brandPrimary"
            hide-details
          ></v-switch>
          <v-switch
            v-model="client.displayLibraryPage"
            label="Display library page"
            color="brandPrimary"
            hide-details
          ></v-switch>

          <h3 class="pt-10 pb-4">Program & Financial Settings</h3>
          <v-switch
            v-if="featureFlags['feature-one10-points']"
            v-model="client.one10PointsEnabled"
            @change="onOne10PointsToggle"
            label="Enable One10 Points"
            color="brandPrimary"
            class="pt-0 mt-0"
          ></v-switch>
          <div v-if="client.one10PointsEnabled">
            <v-switch
              v-model="client.paymentCardEnabled"
              label="Enable Payment Card"
              color="brandPrimary"
              class="pt-0 mt-0 mb-5"
              hint="When off, only points based budgets and awards will be allowed"
              persistent-hint
            ></v-switch>
            <v-text-field
              v-model="client.pointRatio"
              :rules="formRules.amount"
              label="Points to Cash Ratio*"
              color="brandPrimary"
              type="number"
              outlined
              suffix=": $1.00"
            ></v-text-field>
          </div>
          <v-text-field
            ref="programNumberField"
            v-model="client.programNumber"
            :rules="programNumberRules"
            label="Program Number*"
            color="brandPrimary"
            type="text"
            outlined
            @blur="getPspsNumbers"
          ></v-text-field>
          <v-select
            v-model="client.clientClassification"
            :items="clientClassificationOptions"
            :rules="[(v) => !!v || 'Client Classification is required']"
            item-value="value"
            item-text="label"
            label="Client ID Classification*"
            color="brandPrimary"
            outlined
          ></v-select>
          <v-select
            v-if="showPspsNumberSelect"
            v-model="client.pspsNumber"
            :items="pspsNumbers"
            item-text="displayName"
            item-value="pspsNumber"
            :rules="pspsNumberRules"
            :loading="loadingPspsNumbers"
            label="Default PSPS Number*"
            placeholder="Select Default PSPS Number"
            :no-data-text="pspsNoDataText"
            color="brandPrimary"
            outlined
          ></v-select>
          <DynamicInputList
            v-else-if="showPspsInputList"
            :items="pspsNumbers"
            :rules="pspsRules"
            itemKey="pspsNumber"
            :canDelete="() => pspsNumbers.length > 1"
            @add="addPspsNumber"
            @delete="deletePspsNumber"
          />
          <v-text-field
            v-model="client.physicalCardFee"
            :rules="formRules.optionalAmount(true)"
            label="Physical payment card fee"
            color="brandPrimary"
            type="number"
            outlined
            prefix="$"
            placeholder="0"
            hint="Charged once per user for a physical card"
          ></v-text-field>
          <v-text-field
            v-if="displayAnnualPaymentCap"
            v-model="client.annualPaymentCap"
            :rules="formRules.optionalAmount(false)"
            label="Annual Payment Cap"
            color="brandPrimary"
            type="number"
            outlined
            prefix="$"
            placeholder="0"
            hint="Maximum amount a single user can receive per year"
          ></v-text-field>
          <v-text-field
            v-if="client.one10PointsEnabled && isOne10PointsToCardEnabled()"
            v-model="client.minOne10PointsToCashTransferAmount"
            :rules="formRules.optionalAmount(true)"
            label="Minimum Transfer to Card"
            color="brandPrimary"
            type="number"
            outlined
            prefix="$"
            placeholder="0"
            hint="Minimum amount a user can transfer per transaction"
          ></v-text-field>

          <Typography variant="subtitle-1" class="pb-4 pt-2">Reporting Customization</Typography>
          <v-select
            v-model="client.reportColumn1"
            label="Reporting Custom Column 1"
            :items="customReportColumns"
            item-value="value"
            item-text="label"
            color="brandPrimary"
            outlined
          ></v-select>
          <v-select
            v-model="client.reportColumn2"
            label="Reporting Custom Column 2"
            :items="customReportColumns"
            item-value="value"
            item-text="label"
            color="brandPrimary"
            outlined
          ></v-select>
          <v-select
            v-model="client.reportColumn3"
            label="Reporting Custom Column 3"
            :items="customReportColumns"
            item-value="value"
            item-text="label"
            color="brandPrimary"
            outlined
          ></v-select>
          <v-select
            v-model="client.reportColumn4"
            label="Reporting Custom Column 4"
            :items="customReportColumns"
            item-value="value"
            item-text="label"
            color="brandPrimary"
            outlined
          ></v-select>
        </v-form>

        <div class="d-flex flex-row mt-10">
          <Button text color="brandPrimary" @click="close" class="mr-4"> Cancel </Button>
          <Button
            color="brandPrimary"
            :disabled="!form.valid || pspsSelectionBlocked"
            @click="createClient"
          >
            Create Client
          </Button>
        </div>
      </v-card-text>
    </div>

    <!-- Image Cropping dialog -->
    <ImageCropper
      v-if="display.cropper"
      :fileName="imageUpload.fileName"
      :imageUrl="imageUpload.cropUrl"
      :imageisPNG="imageUpload.png"
      @close="resetCroppedImage"
      @success="saveCroppedImage"
    />
    <!-- Dialog used for displaying loading message. -->
    <LoadingDialog
      :showDialog="showLoadingDialog"
      :header="loadingHeaderMessage"
      :line1="loadingMessageLine1"
      :line2="loadingMessageLine2"
    ></LoadingDialog>
  </v-card>
</template>

<script>
import ClientService from "@/services/ClientServiceV2";
import BudgetService from "@/services/BudgetServiceV3";

import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import LoadingDialog from "@/components/loadingDialog.vue";
import ImageCropper from "@/components/ImageCropper.vue";
import DynamicInputList from "@/components/generic/form-inputs/DynamicInputList.vue";
import WalletService from "@/services/WalletService";

import { mapState } from "vuex";

import { formRules, userColumns, clientClassificationOptions } from "@/shared_data/data.js";
import featureFlagsMixin from "@/mixins/featureFlags";

function initialState() {
  return {
    clientService: new ClientService(),
    budgetService: new BudgetService(),

    showLoadingDialog: false,
    loadingHeaderMessage: null,
    loadingMessageLine1: null,
    loadingMessageLine2: null,
    showUploadResults: false,
    formRules,
    form: {
      valid: false,
    },
    client: {
      clientId: null,
      clientName: null,
      emailDomain: null,
      initialRouteOnLoad: "wallet",
      onboardingLanguage: "PAYMENTS",
      parentClientId: null,
      physicalCardFee: null,
      annualPaymentCap: null,
      // Leave onboardingToken as undefined so it autocreates a link
      // onboardingToken: null,
      hideDefaultPlatformLogo: false,
      // paid: false,
      displayUniversalLearning: true,
      displayMobilePopup: true,
      magicLinksEnabled: true,
      displayWalletPage: true,
      displayProgramPage: true,
      displayProgramDashboardPage: false,
      displayClaimPage: false,
      displayLibraryPage: true,
      displayActivityPage: true,
      reportColumn1: null,
      reportColumn2: null,
      reportColumn3: null,
      reportColumn4: null,

      // One10 Points
      one10PointsEnabled: false,
      paymentCardEnabled: true,
      programNumber: null,
      pointRatio: null,
      minOne10PointsToCashTransferAmount: null,
      clientClassification: null,
      pspsNumber: null,
    },
    pspsNumbers: [],
    loadingPspsNumbers: false,
    pspsLookupEmpty: false,
    pspsLookupTimeout: null,
    pspsRequestId: 0,
    lastPspsLookup: null,
    clientClassificationOptions,
    defaultPages: [
      { label: "Activity", value: "activity" },
      { label: "Learning", value: "learning" },
      { label: "Wallet", value: "wallet" },
      { label: "Programs", value: "programs" },
      { label: "Dashboard", value: "program-dashboard" },
    ],
    onboardingLanguages: [
      { label: "Payments", value: "PAYMENTS" },
      { label: "Learning", value: "LEARNING" },
    ],
    customReportColumns: userColumns,

    loading: {
      blacklist: false,
    },
    imageUpload: {
      file: null,
      imageUrl: null,
      cropUrl: null,
      fileName: null,
      png: false,
    },
    display: {
      cropper: false,
    },
  };
}

export default {
  name: "ClientCreatorWidget",
  components: {
    DrawerToolbar,
    Button,
    Typography,
    LoadingDialog,
    ImageCropper,
    DynamicInputList,
  },
  props: {
    clientId: Number,
  },
  data() {
    return initialState();
  },
  created() {
    this.seedLegacyPspsNumbers();
  },
  mounted() {},
  mixins: [featureFlagsMixin],
  methods: {
    close() {
      this.$emit("close");
    },
    getClients(e) {
      this.$emit("get-clients", e);
    },
    resetForm() {
      Object.assign(this.$data, initialState());
      this.seedLegacyPspsNumbers();
    },
    // The legacy list renders one text field per entry, so it needs a blank row to show anything
    seedLegacyPspsNumbers() {
      if (!this.isPspsLookupEnabled()) this.pspsNumbers = [{ pspsNumber: "" }];
    },
    displayImageSelector() {
      this.$refs.uploader.click();
    },
    imageFileChanged(e, dragAndDrop = false) {
      const imageRegex = /\.(jpg|jpeg|png|JPG|JPEG|PNG)$/i;
      let file;
      if (dragAndDrop && e.dataTransfer.files.length) file = e.dataTransfer.files[0];
      else if (!dragAndDrop && e.target.files.length) file = e.target.files[0];

      console.log("Image file changed", { e, dragAndDrop, file });
      if (file) {
        console.log("File type: ", file.type);
        console.log("Image Regex test", imageRegex.test(file.name));

        if (
          !(
            // Either the file type is png or jpg
            // Or no file type and the extension is png/jpg
            (
              (file.type &&
                file.type.includes("image/") &&
                (file.type == "image/png" || file.type == "image/jpeg")) ||
              (!file.type && imageRegex.test(file.name))
            )
          )
        ) {
          this.$store.dispatch("showError", {
            title: "Invalid file type",
            message: "Please upload a valid image file (PNG or JPG)",
          });
          return;
        }

        // Load file into temp variable for cropping
        this.imageUpload.cropUrl = URL.createObjectURL(file);
        this.imageUpload.fileName = file.name;
        this.display.cropper = true;
        // A missing type still gets here when the name is .png/.jpg/.jpeg. Trust
        // that extension, or a typeless PNG is JPEG-encoded and loses transparency.
        this.imageUpload.png =
          file.type == "image/png" || (!file.type && /\.png$/i.test(file.name));
      }

      //This wipes the files in the uploader component so it detects a change even if you select the same image twice
      var uploader = document.querySelector("#uploader");
      console.log("Wiping uploader", uploader);
      if (uploader) uploader.value = "";
    },
    resetCroppedImage() {
      this.imageUpload = {
        file: null,
        imageUrl: null,
        cropUrl: null,
        fileName: null,
      };
      this.display.cropper = false;
    },
    saveCroppedImage(e) {
      console.log("saveCroppedImage", { e });
      this.resetCroppedImage();
      const { file, imageUrl } = e;
      this.imageUpload.file = file;
      this.imageUpload.imageUrl = imageUrl;
    },
    deleteImage() {
      this.client.companyPicUrl = null;
      this.resetCroppedImage();
    },
    addPspsNumber() {
      this.pspsNumbers.push({ pspsNumber: "" });
    },
    deletePspsNumber(index) {
      this.pspsNumbers.splice(index, 1);
    },
    queuePspsLookup() {
      clearTimeout(this.pspsLookupTimeout);
      this.pspsLookupTimeout = setTimeout(this.getPspsNumbers, 500);
    },
    // Vuetify treats any two rule functions as equal, so a changed rules array never re-runs on its own
    revalidateProgramNumber() {
      this.$nextTick(() => this.$refs.programNumberField?.validate());
    },
    async getPspsNumbers() {
      // Covers the program number blur handler and both watchers in one place
      if (!this.isPspsLookupEnabled()) return;

      clearTimeout(this.pspsLookupTimeout);

      const programNumber = (this.client.programNumber || "").trim();
      const lookupKey = `${programNumber}|${this.isCashClient}`;

      // The program number field also refetches on blur, so bail when nothing changed. Otherwise
      // clicking another field wipes a valid selection and flashes the required-field error.
      // An empty lookup in between clears the list without settling the cache, so the key only
      // means "already loaded" while those results are still on screen.
      if (lookupKey === this.lastPspsLookup && this.pspsNumbers.length) return;

      // Ignore responses from lookups the user has already typed past
      const requestId = ++this.pspsRequestId;

      this.pspsNumbers = [];
      this.client.pspsNumber = null;
      this.pspsLookupEmpty = false;

      if (!this.isCashClient || !programNumber) {
        this.lastPspsLookup = lookupKey;
        this.loadingPspsNumbers = false;
        this.revalidateProgramNumber();
        return;
      }

      this.loadingPspsNumbers = true;
      try {
        const response = await WalletService.getRewardsClientPspsNumbersV2(
          null,
          "CASH",
          programNumber
        );
        if (requestId !== this.pspsRequestId) return;

        this.pspsNumbers = (response || []).map((x) => ({
          ...x,
          displayName: x.description ? `${x.pspsNumber} - ${x.description}` : x.pspsNumber,
        }));
        this.pspsLookupEmpty = this.pspsNumbers.length === 0;
        // Only settle the cache on a lookup that returned something, so an empty one can be retried
        if (!this.pspsLookupEmpty) this.lastPspsLookup = lookupKey;
        if (this.pspsNumbers.length === 1) {
          this.client.pspsNumber = this.pspsNumbers[0].pspsNumber;
        }
      } catch (err) {
        if (requestId !== this.pspsRequestId) return;
        console.log("Error loading PSPS numbers", err);
        this.pspsNumbers = [];
        this.pspsLookupEmpty = true;
      } finally {
        if (requestId === this.pspsRequestId) {
          this.loadingPspsNumbers = false;
          this.revalidateProgramNumber();
        }
      }
    },
    onOne10PointsToggle(enabled) {
      // The payment card switch is hidden while One10 Points is off, so restore the cash default
      this.client.paymentCardEnabled = !enabled;
    },
    async createClient() {
      try {
        this.showLoadingDialog = true;
        this.loadingHeaderMessage = "Creating client";
        this.loadingMessageLine1 = "Please wait...";
        // Copy the payload so stripping fields does not clear the live form and trip its validation
        const pspsNumber = this.client.pspsNumber;
        const client = { ...this.client, systemTenantId: this.client.clientName };
        delete client.clientId;
        // PSPS numbers live on BudgetPSPSNumbers, not on the client record
        delete client.pspsNumber;

        console.log(client);
        if (
          (client.physicalCardFee && parseFloat(client.physicalCardFee) < 0) ||
          client.physicalCardFee === null
        )
          client.physicalCardFee = 0;

        // Only include pointRatio when One10 Points is enabled
        if (!client.one10PointsEnabled) {
          delete client.pointRatio;
        }

        // client.physicalCardFee = parseFloat(client.physicalCardFee);
        const response = await this.clientService.createClientV3(client);
        console.log("Client creation response ", response);
        const clientId = response?.result?.[0]?.clientId;
        this.client.clientId = clientId;

        // Races the client creation webhook that stamps the default General Funds budget, so run
        // it before the slower image upload. Nothing repairs the budget if the webhook still wins.
        if (clientId) {
          await this.createPSPSNumbers(clientId, pspsNumber);
        }

        if (clientId && this.imageUpload.file) {
          await this.uploadClientImage();
        }

        this.getClients({ clientId });

        // We also refresh the client array used on the other pages
        // Delay it 3s because the cache gets updated via events
        setTimeout(() => {
          this.$store.dispatch("setClients");
        }, 3000);
        this.close();
      } catch (err) {
        console.log("Client create error ", err);
        this.$store.dispatch("showError", {
          title: "Client creation failed",
          message: err?.message || "Failed to create client. Please try again.",
        });
      } finally {
        this.showLoadingDialog = false;
      }
    },
    async createPSPSNumbers(clientId, pspsNumber) {
      try {
        const payload = this.isPspsLookupEnabled()
          ? [pspsNumber].filter(Boolean).map((n) => ({ clientId, pspsNumber: n }))
          : this.pspsNumbers
              .filter((n) => n.pspsNumber && n.pspsNumber.trim() !== "")
              .map((n) => ({ clientId, pspsNumber: n.pspsNumber }));

        if (payload.length) {
          await this.budgetService.createBudgetPSPSNumbers(payload);
        }
      } catch (err) {
        console.log("Error creating PSPS numbers", err);
      }
    },
    async uploadClientImage() {
      const response = await this.clientService.uploadClientImageV3(
        this.client.clientId,
        this.imageUpload.file
      );
      console.log("Client image upload response ", response);
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "permissions", "featureFlags"]),
    displayAnnualPaymentCap() {
      return !!(this.permissions || []).find((x) => x == "users:create:annualpaymentcap");
    },
    isCashClient() {
      return this.client.paymentCardEnabled === true;
    },
    // A lone number is auto selected, but the field stays visible so the value is always shown
    showPspsNumberSelect() {
      return this.isPspsLookupEnabled() && this.isCashClient;
    },
    showPspsInputList() {
      return !this.isPspsLookupEnabled();
    },
    pspsSelectionBlocked() {
      return this.isPspsLookupEnabled() && this.isCashClient && !this.client.pspsNumber;
    },
    programNumberRules() {
      if (!this.isPspsLookupEnabled()) return this.formRules.stringRequired(255);
      return [
        ...this.formRules.stringRequired(255),
        () => !this.pspsLookupEmpty || "Please enter valid Program Number",
      ];
    },
    pspsRules() {
      return [
        ...this.formRules.stringRequired(255),
        ...this.formRules.preventDuplicateStrings(
          this.pspsNumbers,
          "pspsNumber",
          "is a duplicate PSPS number"
        ),
      ];
    },
    pspsNumberRules() {
      return [(v) => !!v || "Default PSPS Number is required"];
    },
    pspsNoDataText() {
      if (!(this.client.programNumber || "").trim()) {
        return "Enter a Program Number to load PSPS numbers";
      }
      return "No PSPS numbers found for this Program Number";
    },
  },
  watch: {
    "client.programNumber"() {
      this.queuePspsLookup();
    },
    "client.paymentCardEnabled"() {
      this.getPspsNumbers();
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped></style>
